import dayjs from 'dayjs';
import _ from 'lodash';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrderAPI } from '../../apis/order-api';
import { PaymentPixQRCodeModalBody } from '../../components/payment/payment-pix-qrcode-modal-body';
import { PaymentPixQRCodeModalTitle } from '../../components/payment/payment-pix-qrcode-modal-title';
import { useErrorHandler } from '../../hooks/use-error-handler';
import { useFeature } from '../../hooks/use-feature';
import { usePaymentInterval } from '../../hooks/use-payment-interval';
import { useShake } from '../../hooks/use-shake';
import { useStorage } from '../../hooks/use-storage';
import { useSwal } from '../../hooks/use-swal';
import { copyToClipboard } from '../../utils/copy-to-clipboard';
import { layoutDefaults } from '../../utils/layout-defaults';
import { parsePaymentStatus } from '../../utils/manager/payment-manager';
import { usePaymentPixManager } from '../../utils/manager/payment-pix-manager';
import { useAnalytics } from '../../utils/setup/analytics-setup';

function usePaymentPixQRCodeModal() {
  const { analyticsPageView, analyticsFeatureUseEvent } = useAnalytics();
  const feature = useFeature();
  const storage = useStorage();
  const { t } = useTranslation();
  const { setPayment, paymentRef, spot, menuInfo } = storage;
  const { handleErrorOnModal } = useErrorHandler();
  const { findPaymentOrdersFromRemote } = useOrderAPI();
  const confirmButtonTextRef = useRef();
  const { swal } = useSwal({ storage });

  const { generateAndStorePaymentPix, cancelPaymentPix } =
    usePaymentPixManager();

  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const ordersRef = useRef();
  const tipAmountRef = useRef();
  const wasCancelledRef = useRef(false);
  const wasPaidRef = useRef(false);

  // async function playSound() {
  //     setTimeout(() => {
  //         play();
  //     }, 0);
  // }

  const onPaymentPaid = async () => {
    wasPaidRef.current = true;
    swal.close();
  };

  const onPaymentCancelled = async () => {
    wasCancelledRef.current = true;
    swal.close();
  };

  usePaymentInterval(
    {
      payment: paymentRef.current,
      orders: ordersRef.current,
      onPaymentPaid,
      onPaymentCancelled,
    },
    focused && !loading,
    [focused, loading]
  );

  useShake(
    {
      element: confirmButtonTextRef?.current?.parentElement,
      enabled: !loading,
    },
    [loading]
  );

  async function prepare({ orders, tipAmount }) {
    try {
      ordersRef.current = orders ?? paymentRef.current.orders;
      tipAmountRef.current = tipAmount;

      if (
        paymentRef.current != null &&
        _.isEmpty(paymentRef.current.orders?.filter((o) => o != null))
      ) {
        const result = await findPaymentOrdersFromRemote(paymentRef.current);
        ordersRef.current = result.data;

        setPayment({
          ...paymentRef.current,
          orders: ordersRef.current,
        });
      }

      if (
        paymentRef.current == null ||
        (parsePaymentStatus(paymentRef.current) === 'paying' &&
          _.isEmpty(paymentRef.current.tip))
      ) {
        await generateAndStorePaymentPix({
          orders: ordersRef.current,
          tipAmount,
        });
      }
    } catch (error) {
      handleErrorOnModal(error);
    }

    return {};
  }

  const didOpen = () => {
    analyticsPageView('Payment Pix QR Code');
    setFocused(true);
  };

  const onClose = () => {
    setFocused(false);
  };

  const onQRCodeClick = () => {
    swal.clickConfirm();
  };

  async function show({ prepareOnPreConfirm, prepareOnPreDeny }) {
    const timeLimit = 2;
    const secondsPast = dayjs().diff(
      dayjs(paymentRef?.current?.local_time),
      'seconds'
    );

    const result = await swal.fire({
      title: (
        <PaymentPixQRCodeModalTitle
          style={{
            marginTop: layoutDefaults.smallVerticalMargin,
          }}
          title={
            secondsPast <= timeLimit
              ? t('payment-pix-qrcode-modal.title')
              : t('payment-pix-qrcode-modal.title.expired')
          }
          subTitle={
            secondsPast <= timeLimit
              ? t('payment-pix-qrcode-modal.sub-title')
              : t('payment-pix-qrcode-modal.sub-title.expired')
          }
        />
      ),
      html: (
        <PaymentPixQRCodeModalBody
          payment={paymentRef.current}
          feature={feature}
          menuInfo={menuInfo}
          onQRCodeClick={onQRCodeClick}
          analyticsFeatureUseEvent={analyticsFeatureUseEvent}
          spot={spot}
        />
      ),
      showConfirmButton: true,
      showLoaderOnConfirm: true,
      showLoaderOnDeny: true,
      showDenyButton: true,
      preConfirm: () => preConfirm({ prepareOnPreConfirm }),
      preDeny: () => preDeny({ prepareOnPreDeny }),
      confirmButtonText: (
        <span ref={confirmButtonTextRef}>
          {t('payment-pix-qrcode-modal.copy-qrcode')}
        </span>
      ),
      denyButtonText: t('payment-pix-qrcode-modal.give-up'),
      didOpen,
      didDestroy: onClose,
    });

    if (result.isConfirmed) {
      result.copied = copyToClipboard(paymentRef.current.pix.payload);
    }

    if (wasCancelledRef.current) {
      result.isDismissed = true;
      result.isConfirmed = false;
      result.wasCancelled = true;
    }

    if (wasPaidRef.current) {
      result.isDismissed = false;
      result.isConfirmed = true;
      result.wasPaid = true;
    }

    return result;
  }

  const preConfirm = async ({ prepareOnPreConfirm }) => {
    setLoading(true);

    try {
      await prepareOnPreConfirm?.call(this);
    } catch (error) {
      handleErrorOnModal(error);

      setLoading(false);
      return false;
    }

    setLoading(false);
    return true;
  };

  const preDeny = async ({ prepareOnPreDeny }) => {
    setLoading(true);

    try {
      await cancelPaymentPix(paymentRef.current);
      await prepareOnPreDeny?.call(this);
    } catch (error) {
      // if ([404, 409].includes(error.response?.status)) {
      //     setPayment(null);
      //     return null;
      // }

      handleErrorOnModal(error);

      setLoading(false);
      return false;
    }

    setLoading(false);
    return true;
  };

  return {
    preparePaymentPixQRCodeModal: prepare,
    showPaymentPixQRCodeModal: show,
  };
}

export { usePaymentPixQRCodeModal };
